import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import IconVideo from 'components/icons/iconVideo';
import IconClose from 'components/icons/iconClose';
import IconPhoto from 'components/icons/iconPhoto';
import IconRefresh from 'components/icons/iconRefresh';
import { colorRed } from 'styles/layout/colors';
import classNames from 'classnames';
import { GET_S3_PRESIGNED_URL } from 'mocks/queries';
const MediaPreview = ({
  actions: {
    removeAttachment,
    addAttachments
  },
  state: {
    file,
    id
  }
}) => {
  const client = useApolloClient();
  const [s3Path, updateS3Path] = useState(null);
  const [progress, updateProgress] = useState(20);
  const [retry, updateRetry] = useState(0);
  const resetState = () => {
    updateS3Path(null);
    updateProgress(-1);
  };
  const retryLimitExceeded = () => retry > 1;
  const uploadFile = useCallback(() => {
    updateProgress(30);
    client.query({
      query: GET_S3_PRESIGNED_URL,
      fetchPolicy: 'network-only',
      variables: {
        key: file.path,
        section: 'Furniture_Issue'
      }
    }).then(s3PresignedUrl => {
      updateProgress(40);
      if (s3PresignedUrl?.data?.getS3presignedUrl?.success) {
        fetch(s3PresignedUrl?.data?.getS3presignedUrl?.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'public-read'
          }
        }).then(response => {
          updateProgress(50);
          return response;
        }).then(response => {
          if (response.status === 200 && response?.url) {
            updateS3Path(response.url);
            updateProgress(100);
          } else {
            updateProgress(-1);
          }
        }).catch(() => {
          resetState();
        });
      } else {
        resetState();
      }
    }).catch(() => {
      resetState();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.path, retry]);
  const retryUpload = () => {
    if (retryLimitExceeded() === false) {
      updateRetry(retry + 1);
      uploadFile();
    }
  };
  useEffect(() => {
    if (file?.path) {
      uploadFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.path]);
  useEffect(() => {
    addAttachments(id, s3Path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s3Path]);
  if (!file) return null;
  return <li key={file.path} className={classNames('text-xs font-bold flex text-left rounded-sm relative items-center text-brand-dark no-underline py-2 pr-12 pl-4 bg-brand-lightest mb-0.5', {
    'text-red border-2 border-red': retryLimitExceeded()
  })}>
      {' '}
      <div className="inline-flex min-w-[56px] shrink-0">
        {file.type.match(/^image\/heic/) && <IconPhoto className="w-[50px] h-[50px] mr-2.5 object-contain" />}

        {!file.type.match(/^image\/heic/) && file.type.match(/^image\/*/) && <img src={file.preview} className="w-[50px] h-[50px] mr-2.5 object-contain" alt="" />}

        {file.type.match(/^video\/*/) && <IconVideo className="w-[50px] h-[50px] mr-2.5 object-contain" viewBox="0 0 24 24" fill="none" width="24" height="24" />}
      </div>
      <div>{file.name}</div>
      <div className="top-1/2 right-4 absolute -translate-y-1/2 cursor-pointer">
        {progress === -1 && !retryLimitExceeded() && <IconRefresh width={30} height={30} color={colorRed} onClick={() => retryUpload(id)} />}
        {progress > 0 && progress < 100 && <div>{progress} %</div>}
        {(progress === 100 || retryLimitExceeded()) && <IconClose width={10} height={10} onClick={() => removeAttachment(id)} />}
      </div>
    </li>;
};
MediaPreview.propTypes = {
  actions: PropTypes.shape({
    removeAttachment: PropTypes.func,
    addAttachments: PropTypes.func
  }),
  state: PropTypes.shape({
    file: PropTypes.objectOf(PropTypes.object),
    id: PropTypes.number
  })
};
export default React.memo(MediaPreview);